import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import AnimatedModal from 'components/AnimatedModal';
import useIsMobile from 'hooks/useIsMobile';

import overlayCount from './overlayCount';
import { Transition, Backdrop, Modal, ModalContent, Sidebar, SidebarContent, FixedContainer } from './styles';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface Props {
  show?: boolean;
  width?: number;
  onClose?: () => void;
  hideBackdrop?: boolean;
  sidebar?: boolean;
  backgroundColor?: string;
  children: any;
  testId?: string;
  zIndex?: number;
  sessionToken?:boolean;
}

const ApplicationOverlayContentSessionToken = ({
  show,
  width,
  onClose,
  hideBackdrop,
  sidebar,
  backgroundColor,
  children,
  testId,
  zIndex,
  sessionToken
}: Props) => {
  const didMountRef = useRef(false);
  const incrementedRef = useRef(false);
  const backdropRef = useRef();
  const isMobile = useIsMobile();
  const [isMouseDown, setMouseDown] = useState(false);
  const userData = useSelector((state: RootState) => state.user.accountResult);
  const [bShow, setBShow] = useState(sessionToken ? userData?.bIsSessionToken : show);

  /**
   * Body scroll should be locked whenever there's a modal
   * or a sidebar open.
   */
  useEffect(() => {
    if (isMobile) return;
    if (!userData) return;

    // Avoids first render effect
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (userData?.bIsSessionToken ) {
      overlayCount.increment();
      incrementedRef.current = true;
      setBShow(true);
    } else {
      overlayCount.decrement();
      incrementedRef.current = false;
    }


  }, [userData?.bIsSessionToken , isMobile]);

  useEffect(() => {

    if (!userData?.bIsSessionToken ) setMouseDown(false);
  }, [userData?.bIsSessionToken ]);

  /**
   * Covers the case when this component is unmounted with show
   * set to true.
   */
  useEffect(
    () => () => {
      if (incrementedRef.current) overlayCount.decrement();
    },
    [],
  );

  if (isMobile) {
    return (
      <AnimatedModal show={userData?.bIsSessionToken } onClose={onClose}>
        <FixedContainer $backgroundColor={backgroundColor} data-testid={testId ? `${testId}-content` : undefined}>
          {children}
        </FixedContainer>
      </AnimatedModal>
    );
  }

  const handleBackdropMouseDown = (event: React.MouseEvent) => {
    if (backdropRef.current !== event.target) return;
    setMouseDown(true);
  };

  const handleBackdropMouseUp = (event: React.MouseEvent) => {
    if (backdropRef.current !== event.target || !isMouseDown) {
      setMouseDown(false);
      return;
    }

    if (onClose) onClose();
  };

  const DesktopContainer = sidebar ? Sidebar : Modal;
  const DesktopContent = sidebar ? SidebarContent : ModalContent;

  return createPortal(
    bShow && <Transition
      transitionName={sidebar ? 'right-fade' : 'fade'}
      transitionEnterTimeout={500}
      transitionLeaveTimeout={400}
      style={{ zIndex }}
    >
      {userData?.bIsSessionToken  && (
        <Backdrop
          hide={hideBackdrop}
          ref={backdropRef}
          onMouseDown={handleBackdropMouseDown}
          onMouseUp={handleBackdropMouseUp}
        >
          <DesktopContainer width={width} $backgroundColor={backgroundColor}>
            <DesktopContent data-testid={testId ? `${testId}-content` : undefined}>{children}</DesktopContent>
          </DesktopContainer>
        </Backdrop>
      )}
    </Transition>,
    document.body,
  );
};

export default ApplicationOverlayContentSessionToken;
